import React, { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { Tag } from "primereact/tag";
import { Dialog } from "primereact/dialog";
import toast from "react-hot-toast";

import {
  useGetVenuesQuery,
  useDeleteVenueMutation,
  normalizeVenuesQueryArgs,
  applyVenueListOverrides,
} from "../../store/services/venues";
import { useGetTenantsQuery } from "../../store/services/tenants";
import { useDebounce } from "../../hooks/useDebounce";
import { Venue, VenueStatus } from "../../models/venues";
import { VenueFilters } from "../Filters/VenueFilters";
import { useTenantScope } from "../../hooks/useTenantScope";
import {
  getVenueStatusLabel,
  getVenueStatusSeverity,
} from "../../utils/constants/venues-constants";

type VenuesLocationState = {
  createdVenue?: Venue;
};

export const VenueListDT = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const createdVenue = (location.state as VenuesLocationState | null)?.createdVenue;

  // Filter states
  const [venueName, setVenueName] = useState<string>("");
  const [status, setStatus] = useState<VenueStatus | undefined>();

  const debouncedName = useDebounce(venueName, 500);
  const effectiveName = venueName === "" ? "" : debouncedName;

  // Delete dialog state
  const [venueToDelete, setVenueToDelete] = useState<Venue | undefined>();
  const [removedVenueIds, setRemovedVenueIds] = useState<number[]>([]);

  const venuesQueryArgs = React.useMemo(
    () =>
      normalizeVenuesQueryArgs({
        name: effectiveName || undefined,
        status,
      }),
    [effectiveName, status]
  );

  const { data, isLoading, isFetching, refetch } = useGetVenuesQuery(venuesQueryArgs);

  const listData = React.useMemo(
    () =>
      applyVenueListOverrides(data, {
        createdVenue,
        removedVenueIds,
      }),
    [data, createdVenue, removedVenueIds]
  );

  React.useEffect(() => {
    if (!createdVenue) {
      return;
    }

    void refetch();
  }, [createdVenue, refetch]);

  React.useEffect(() => {
    if (!createdVenue || !data) {
      return;
    }

    if (data.some((venue) => venue.id === createdVenue.id)) {
      navigate(location.pathname, { replace: true, state: null });
    }
  }, [createdVenue, data, navigate, location.pathname]);

  const [deleteVenue, { isLoading: isLoadingDelete }] = useDeleteVenueMutation();
  const { data: tenants } = useGetTenantsQuery({});
  const tenantScope = useTenantScope();

  const tenantMap = React.useMemo(() => {
    const map = new Map<string, string>();
    if (Array.isArray(tenants)) {
      tenants.forEach((t: any) => map.set(String(t.id), t.provider));
    }
    return map;
  }, [tenants]);

  const venuesWithTenantNames = React.useMemo(() => {
    return listData.map((venue) => ({
      ...venue,
      tenantNames: (venue.tenantIds || [])
        .map((id) => tenantMap.get(String(id)) || id)
        .join(", "),
    }));
  }, [listData, tenantMap]);

  const hideDeleteDialog = () => setVenueToDelete(undefined);

  const handleDeleteVenue = (id: number) => {
    deleteVenue(id)
      .unwrap()
      .then(() => {
        setRemovedVenueIds((current) =>
          current.includes(id) ? current : [...current, id]
        );
        toast.success("Venue eliminado exitosamente!");
      })
      .catch((err) => {
        const errorMessage =
          err?.data?.error?.message ||
          err?.data?.errorMessage ||
          "No se pudo eliminar el venue. Puede estar asignado a un evento futuro.";
        toast.error(errorMessage);
      })
      .finally(() => hideDeleteDialog());
  };

  // Column templates
  const statusBodyTemplate = (venue: Venue) => (
    <Tag
      value={getVenueStatusLabel(venue.status)}
      severity={getVenueStatusSeverity(venue.status)}
    />
  );

  const actionsBodyTemplate = (venue: Venue) => (
    <div className="flex gap-2">
      <Button
        icon="pi pi-eye"
        rounded
        text
        severity="info"
        aria-label="Ver"
        onClick={() => navigate(`/venues/${venue.id}`)}
        tooltip="Ver detalle"
        tooltipOptions={{ position: "top" }}
      />
      <Button
        icon="pi pi-pencil"
        rounded
        text
        severity="success"
        aria-label="Editar"
        onClick={() => navigate(`/venues/${venue.id}/edit`)}
        tooltip="Editar"
        tooltipOptions={{ position: "top" }}
      />
      <Button
        icon="pi pi-trash"
        rounded
        text
        severity="danger"
        aria-label="Eliminar"
        onClick={() => setVenueToDelete(venue)}
        tooltip="Eliminar"
        tooltipOptions={{ position: "top" }}
      />
    </div>
  );

  const tenantBodyTemplate = (venue: any) => {
    return venue.tenantNames || "";
  };

  return (
    <>
      <VenueFilters
        venueName={venueName}
        setVenueName={setVenueName}
        status={status}
        setStatus={setStatus}
      />
      <DataTable
        value={venuesWithTenantNames}
        loading={isLoading || isLoadingDelete || (Boolean(createdVenue) && isFetching)}
        paginator
        rows={10}
        rowsPerPageOptions={[5, 10, 25, 50]}
        emptyMessage="No se encontraron venues"
        stripedRows
        sortField="name"
        sortOrder={1}
        removableSort
        dataKey="id"
      >
        <Column field="name" header="Nombre" sortable style={{ minWidth: "14rem" }} />
        <Column field="address" header="Dirección" sortable style={{ minWidth: "18rem" }} />
        {tenantScope.role === "SUPER_ADMIN" && !tenantScope.tenantId && (
          <Column
            header="Tenant"
            body={tenantBodyTemplate}
            style={{ minWidth: "10rem" }}
          />
        )}
        <Column
          field="status"
          header="Estado"
          body={statusBodyTemplate}
          sortable
          style={{ minWidth: "8rem" }}
        />
        <Column
          header="Acciones"
          body={actionsBodyTemplate}
          style={{ minWidth: "12rem" }}
          exportable={false}
        />
      </DataTable>

      {/* Delete confirmation dialog */}
      <Dialog
        visible={!!venueToDelete}
        style={{ width: "450px" }}
        header="Confirmar eliminación"
        modal
        footer={
          <div>
            <Button
              label="Cancelar"
              icon="pi pi-times"
              outlined
              onClick={hideDeleteDialog}
            />
            <Button
              label="Eliminar"
              icon="pi pi-trash"
              severity="danger"
              onClick={() => venueToDelete && handleDeleteVenue(venueToDelete.id)}
              loading={isLoadingDelete}
            />
          </div>
        }
        onHide={hideDeleteDialog}
      >
        <div className="flex align-items-center gap-3">
          <i
            className="pi pi-exclamation-triangle text-4xl text-yellow-500"
          />
          <span>
            ¿Estás seguro de que deseas eliminar el venue{" "}
            <strong>{venueToDelete?.name}</strong>?
            <br />
            <small className="text-color-secondary">
              Si está asignado a un evento futuro o en curso, la eliminación será bloqueada.
            </small>
          </span>
        </div>
      </Dialog>
    </>
  );
};
